/**
 * The only way this app talks to a server.
 *
 * Two rules are enforced here rather than left to convention:
 *
 * 1. Every request goes to our own Worker. There is no code path from the
 *    browser to Jeabs (spec sections 67 and 71), and no Jeabs credential
 *    exists in this bundle to make one with.
 * 2. Every response is parsed through the shared Zod contract in
 *    `@kso/schemas`. A backend that drifts produces a visible, named error
 *    instead of a screen quietly rendering `undefined`.
 */

import type { z } from 'zod';
import { apiErrorSchema } from '@kso/schemas';
import { tr } from '../i18n';

export type ApiErrorKind =
  | 'UNAUTHENTICATED'
  | 'FORBIDDEN'
  | 'NOT_FOUND'
  | 'RATE_LIMIT'
  | 'CONTRACT_MISMATCH'
  | 'NETWORK'
  | 'INTERNAL'
  | string;

export class ApiError extends Error {
  readonly kind: ApiErrorKind;
  readonly status: number | null;
  /** Extra operator-facing detail. Never contains upstream credentials. */
  readonly detail: string | null;

  constructor(kind: ApiErrorKind, message: string, status: number | null, detail?: string) {
    super(message);
    this.name = 'ApiError';
    this.kind = kind;
    this.status = status;
    this.detail = detail ?? null;
  }

  /** True when the route simply is not built yet, which is a normal state
   *  during rollout and should render as "not available", not as a failure. */
  get isMissingRoute(): boolean {
    return this.status === 404 || this.status === 501;
  }
}

export type TokenProvider = () => Promise<string | null>;

export type ApiClient = {
  get<T extends z.ZodTypeAny>(path: string, schema: T): Promise<z.infer<T>>;
  post<T extends z.ZodTypeAny>(path: string, body: unknown, schema: T): Promise<z.infer<T>>;
  put<T extends z.ZodTypeAny>(path: string, body: unknown, schema: T): Promise<z.infer<T>>;
  patch<T extends z.ZodTypeAny>(path: string, body: unknown, schema: T): Promise<z.infer<T>>;
  /**
   * DELETE still parses a response body: the routes that use it soft-cancel and
   * return the updated record rather than emptying anything (spec section 1.4).
   */
  delete<T extends z.ZodTypeAny>(path: string, schema: T): Promise<z.infer<T>>;
};

export function createApiClient(baseUrl: string, getToken: TokenProvider): ApiClient {
  async function request<T extends z.ZodTypeAny>(
    method: 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE',
    path: string,
    schema: T,
    body?: unknown,
  ): Promise<z.infer<T>> {
    const token = await getToken();
    if (!token) {
      throw new ApiError('UNAUTHENTICATED', tr().api.sessionExpired, 401);
    }

    const headers: Record<string, string> = { Authorization: `Bearer ${token}` };
    if (body !== undefined) headers['Content-Type'] = 'application/json';

    let response: Response;
    try {
      response = await fetch(`${baseUrl}${path}`, {
        method,
        headers,
        body: body === undefined ? undefined : JSON.stringify(body),
      });
    } catch {
      throw new ApiError('NETWORK', tr().api.unreachable, null);
    }

    if (!response.ok) throw await toApiError(response);

    let payload: unknown;
    try {
      payload = await response.json();
    } catch {
      throw new ApiError('CONTRACT_MISMATCH', tr().api.notJson, response.status);
    }

    const parsed = schema.safeParse(payload);
    if (!parsed.success) {
      // Surfaced rather than swallowed: rendering a partially-understood
      // payload is how a dashboard starts showing numbers it cannot justify.
      throw new ApiError(
        'CONTRACT_MISMATCH',
        tr().api.contractMismatch(path),
        response.status,
        parsed.error.issues
          .slice(0, 5)
          .map((issue) => `${issue.path.join('.') || '(root)'}: ${issue.message}`)
          .join('; '),
      );
    }

    return parsed.data;
  }

  return {
    get: (path, schema) => request('GET', path, schema),
    post: (path, body, schema) => request('POST', path, schema, body),
    put: (path, body, schema) => request('PUT', path, schema, body),
    patch: (path, body, schema) => request('PATCH', path, schema, body),
    delete: (path, schema) => request('DELETE', path, schema),
  };
}

/**
 * The Worker sanitizes upstream errors before they get here (spec section 67),
 * so whatever `message` it sends is safe to display verbatim.
 */
async function toApiError(response: Response): Promise<ApiError> {
  let body: unknown;
  try {
    body = await response.json();
  } catch {
    body = null;
  }

  const parsed = apiErrorSchema.safeParse(body);
  if (parsed.success) {
    return new ApiError(parsed.data.error.kind, parsed.data.error.message, response.status);
  }

  const t = tr().api;
  if (response.status === 401) {
    return new ApiError('UNAUTHENTICATED', t.sessionExpired, 401);
  }
  if (response.status === 403) {
    return new ApiError('FORBIDDEN', t.forbidden, 403);
  }
  if (response.status === 404) {
    return new ApiError('NOT_FOUND', t.notFound, 404);
  }
  return new ApiError('INTERNAL', t.httpStatus(response.status), response.status);
}

/** Builds a query string, omitting empty values so URLs stay stable cache keys. */
export function query(params: Record<string, string | number | boolean | null | undefined>): string {
  const search = new URLSearchParams();
  for (const [key, value] of Object.entries(params)) {
    if (value === null || value === undefined || value === '') continue;
    search.set(key, String(value));
  }
  const serialized = search.toString();
  return serialized ? `?${serialized}` : '';
}
